import { ReactNode, RefObject, useCallback, useRef, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Popover as HeadlessPopover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { Button } from './Button';
import {
  calculatePopoverCoordinates,
  type PopoverPosition,
  type PopoverRect,
} from './popover-position';

/**
 * Translate a click-time viewport rect by the scroll delta since it was
 * captured, so a cursor-anchored panel follows the content it anchored to
 * across scroll/resize recalculations.
 */
function translatedAnchorRect(
  anchor: PopoverRect,
  capturedScrollX: number,
  capturedScrollY: number
): PopoverRect {
  return {
    ...anchor,
    left: anchor.left + (window.scrollX - capturedScrollX),
    right: anchor.right + (window.scrollX - capturedScrollX),
    top: anchor.top + (window.scrollY - capturedScrollY),
    bottom: anchor.bottom + (window.scrollY - capturedScrollY),
  };
}

function anchorRectToPopoverRect(anchor: { left: number; top: number; width: number; height: number }): PopoverRect {
  return {
    left: anchor.left,
    top: anchor.top,
    right: anchor.left + anchor.width,
    bottom: anchor.top + anchor.height,
    width: anchor.width,
    height: anchor.height,
  };
}

export type { PopoverPosition } from './popover-position';

export type PopoverProps = {
  trigger?: ReactNode;
  children: ReactNode | ((props: { open: boolean; close: () => void }) => ReactNode);
  className?: string;
  panelRef?: RefObject<HTMLDivElement | null>;
  portal?: boolean;
  modal?: boolean;
  position?: PopoverPosition;
  /** Explicit anchor rect (e.g. the cursor point at click time) used instead
   *  of the trigger element's rect for positioning. Optional; existing
   *  callers keep the trigger-rect behavior when omitted. */
  anchorRect?: { left: number; top: number; width: number; height: number } | null;
  portalTargetId?: string;
  renderTrigger?: (props: { open: boolean }) => ReactNode;
  panelClassName?: string;
  onOpenChange?: (open: boolean) => void;
  /** Optional controlled state for cursor-anchored interaction panels. */
  open?: boolean;
};

// Position class mappings for fixed positioning
const positionClasses: Record<PopoverPosition, string> = {
  'bottom-start': 'top-full left-0 mt-2',
  'bottom-center': 'top-1/2 left-1/2 -translate-x-1/2 mt-2',
  'bottom-end': 'top-full right-0 mt-2',
  'top-start': 'bottom-full left-0 mb-2',
  'top-center': 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  'top-end': 'bottom-full right-0 mb-2',
  'left-start': 'right-full top-0 mr-2',
  'left-center': 'right-full top-1/2 -translate-y-1/2 mr-2',
  'left-end': 'right-full bottom-0 mr-2',
  'right-start': 'left-full top-0 ml-2',
  'right-center': 'left-full top-1/2 -translate-y-1/2 ml-2',
  'right-end': 'left-full bottom-0 ml-2',
};

// Transform origin for animations
const transformOrigins: Record<PopoverPosition, string> = {
  'bottom-start': 'origin-top-left',
  'bottom-center': 'origin-top',
  'bottom-end': 'origin-top-right',
  'top-start': 'origin-bottom-left',
  'top-center': 'origin-bottom',
  'top-end': 'origin-bottom-right',
  'left-start': 'origin-right-top',
  'left-center': 'origin-right',
  'left-end': 'origin-right-bottom',
  'right-start': 'origin-left-top',
  'right-center': 'origin-left',
  'right-end': 'origin-left-bottom',
};

function OpenChangeNotifier({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const onOpenChangeRef = useRef(onOpenChange);
  const hasMountedRef = useRef(false);
  onOpenChangeRef.current = onOpenChange;

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    onOpenChangeRef.current?.(open);
  }, [open]);
  return null;
}

export const Popover = ({
  trigger,
  children,
  className = '',
  panelRef,
  portal = true,
  modal = false,
  position = 'bottom-start',
  anchorRect,
  portalTargetId,
  renderTrigger,
  panelClassName,
  onOpenChange,
  open: controlledOpen,
}: PopoverProps) => {
  const buttonRef = useRef<HTMLSpanElement>(null);
  const internalPanelRef = useRef<HTMLDivElement>(null);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const [panelPositioned, setPanelPositioned] = useState(!portal);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  // Cursor-anchored panels pass a click-time viewport rect; keep it with the
  // scroll position at capture time so recalcs translate it with the content.
  const anchorStateRef = useRef<{ rect: PopoverRect | null; scrollX: number; scrollY: number }>({
    rect: null,
    scrollX: 0,
    scrollY: 0,
  });

  useLayoutEffect(() => {
    anchorStateRef.current = anchorRect
      ? {
          rect: anchorRectToPopoverRect(anchorRect),
          scrollX: window.scrollX,
          scrollY: window.scrollY,
        }
      : { rect: null, scrollX: 0, scrollY: 0 };
    if (anchorRect) {
      // Give the panel a useful first frame before the measured panel size is
      // available. Without this, a fixed portaled panel briefly renders at
      // (0, 0), which is especially visible in the Memory Evidence popover.
      setPanelStyle((current) => ({
        ...current,
        left: anchorRect.left,
        top: anchorRect.top + anchorRect.height + 8,
      }));
      setPanelPositioned(true);
    }
  }, [anchorRect]);

  const setPanelRefs = useCallback(
    (node: HTMLDivElement | null): void => {
      internalPanelRef.current = node;
      if (panelRef) panelRef.current = node;
    },
    [panelRef]
  );

  const handleOpenChange = useCallback(
    (open: boolean): void => {
      setIsOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange]
  );

  useEffect(() => {
    if (controlledOpen === undefined || controlledOpen === isOpen) return;
    buttonRef.current?.click();
  }, [controlledOpen, isOpen]);

  // Find portal target element
  useEffect(() => {
    if (!portalTargetId) return;

    const target = document.getElementById(portalTargetId);
    setPortalTarget(target);
  }, [portalTargetId]);

  // Keep portaled panels inside the viewport. Position is recalculated after
  // opening and on viewport or content-size changes so every recalculation
  // uses the requested placement and the current measured panel dimensions.
  useLayoutEffect(() => {
    if (!portal || !isOpen || !buttonRef.current || !internalPanelRef.current) return;

    let animationFrame: number | null = null;

    // A portaled panel has no useful CSS position until its trigger and panel
    // have both been measured. Keep it hidden through that first frame so it
    // never flashes at the portal's default position.
    // Cursor-anchored panels already have a usable first-frame position from
    // the anchorRect effect above. Keeping them visible avoids a hidden state
    // between the click and the next animation frame.
    if (!anchorRect) setPanelPositioned(false);

    const recalcPosition = (): void => {
      const panel = internalPanelRef.current;
      if (!panel) return;
      const anchor = anchorStateRef.current;
      const rect = anchor.rect
        ? translatedAnchorRect(anchor.rect, anchor.scrollX, anchor.scrollY)
        : buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const panelRect = panel.getBoundingClientRect();
      const coordinates = calculatePopoverCoordinates({
        trigger: rect,
        panelWidth: panelRect.width,
        panelHeight: panelRect.height,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        position,
      });
      setPanelStyle({
        top: coordinates.top,
        left: coordinates.left,
        maxWidth: coordinates.maxWidth,
        maxHeight: coordinates.maxHeight,
        overflowY: 'auto',
      });
      setPanelPositioned(true);
    };

    const queueRecalc = (): void => {
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(recalcPosition);
    };

    const handleScroll = (event: Event): void => {
      const eventTarget = event.target;
      if (eventTarget instanceof Node && internalPanelRef.current?.contains(eventTarget)) {
        return;
      }
      queueRecalc();
    };

    queueRecalc();
    window.addEventListener('resize', queueRecalc);
    window.addEventListener('scroll', handleScroll, true);
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(queueRecalc);
    resizeObserver?.observe(internalPanelRef.current);

    return () => {
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', queueRecalc);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [anchorRect, isOpen, portal, position, portalTarget]);

  const renderPanelContent = (open: boolean) => (
    <PopoverPanel
      ref={setPanelRefs}
      portal={portal && !portalTargetId}
      modal={modal}
      transition
      // Unmount portaled panels with their owner. Keeping a closed panel
      // mounted can leave a stale filter menu visible after route changes.
      unmount={true}
      className={`
        glass-surface
        rounded-xl
        ${portal ? 'fixed pointer-events-auto' : `absolute ${positionClasses[position]}`}
        ${transformOrigins[position]}
        ${panelClassName || 'w-80 dropdown-panel-light p-4'}
        focus:outline-none
        transition duration-200 ease-out
        data-[closed]:scale-95 data-[closed]:opacity-0
        z-[9999]
      `}
      onClick={(event) => event.stopPropagation()}
      style={portal ? { ...panelStyle, visibility: panelPositioned ? 'visible' : 'hidden' } : undefined}
    >
      {({ close }) => (
        <>{typeof children === 'function' ? children({ open, close }) : children}</>
      )}
    </PopoverPanel>
  );

  return (
    <HeadlessPopover className={`relative ${className}`}>
      {({ open }) => (
        <>
          <OpenChangeNotifier open={open} onOpenChange={handleOpenChange} />
          {renderTrigger ? (
            <PopoverButton
              as="span"
              ref={buttonRef as React.Ref<HTMLButtonElement>}
              className="focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
            >
              {renderTrigger({ open })}
            </PopoverButton>
          ) : (
            <PopoverButton
              as="span"
              ref={buttonRef as React.Ref<HTMLButtonElement>}
              className="focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
            >
              <Button
                variant="secondary"
                size="small"
                icon={
                  <svg
                    className={`h-4 w-4 text-white/60 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                }
                iconPosition="right"
                className="bg-white/5 backdrop-blur-xl hover:bg-white/10"
              >
                {trigger}
              </Button>
            </PopoverButton>
          )}

          {portalTargetId && portalTarget
            ? createPortal(renderPanelContent(open), portalTarget)
            : renderPanelContent(open)}
        </>
      )}
    </HeadlessPopover>
  );
};
